// Centralized helpers for handling API errors returned by the backend

export type ApiError = {
  code?: string;
  description?: string;
  path?: string;
  time?: string;
};

// Extract Error[] from common error shapes (Axios, fetch wrappers, raw throws)
export function extractApiErrors(err: any): ApiError[] {
  const from =
    err?.errors ||
    err?.payload?.errors ||
    err?.response?.data?.errors ||
    err?.response?.errors ||
    err?.data?.errors;

  if (Array.isArray(from) && from.length) return from;

  // Single error object fallbacks
  const single = err?.response?.data || err?.data || err;
  if (single?.code || single?.description) return [single];
  return [];
}

// Turn errors into localized user-facing strings
export function getErrorMessages(
  err: any,
  t: (key: string, opts?: any) => string,
  fallback?: string
): string[] {
  const errors = extractApiErrors(err);
  if (errors.length) {
    return errors.map((e) => {
      const code = e.code ?? '';
      const key = code ? `errors:${code}` : '';
      const localized = key ? t(key) : '';
      // Robust missing-translation detection:
      // i18next may return the key ("errors:PermissionDenied") OR the raw code ("PermissionDenied") when a key is missing,
      // depending on fallbackNS/defaultNS config. Treat both as missing and fall back to backend description.
      const looksLikeMissing = !localized || localized === key || (!!code && localized === String(code));
      const message = looksLikeMissing ? (e.description || '') : localized;
      return message || fallback || t('common:Something went wrong');
    });
  }

  // No structured backend errors were found. Infer a helpful message from common HTTP/network shapes.
  const status: number | undefined =
    err?.response?.status ??
    err?.status ??
    err?.response?.data?.status;

  const rawMessage: string | undefined =
    err?.response?.data?.message ||
    err?.response?.data?.title ||
    err?.message;

  const hasResponse = !!err?.response;
  const isNetwork = !hasResponse && (!!err?.request || String(rawMessage).toLowerCase().includes('network'));

  if (isNetwork) {
    return [
      fallback ||
        t('Could not reach the server. Please check your internet connection and try again.'),
    ];
  }

  if (typeof status === 'number') {
    switch (status) {
      case 400:
        return [fallback || rawMessage || t('Some of the entered data is invalid. Please review and try again.')];
      case 401:
        return [t('Your session has expired. Please sign in again and retry.')];
      case 403:
        return [t("You don't have permission to perform this action.")];
      case 404:
        return [t('The requested resource was not found. Please refresh and try again.')];
      case 409:
        return [t('Your changes conflict with another update. Please refresh and try again.')];
      default:
        if (status >= 500) {
          return [t('Server error while saving. Please try again in a moment.')];
        }
        break;
    }
  }

  if (rawMessage && typeof rawMessage === 'string' && rawMessage.trim()) {
    return [rawMessage];
  }

  return [fallback || t('common:Something went wrong')];
}

// Convenience for snackbars (matches current snackbar severity union in the app)
export function showApiError(
  setSnackbar: (s: { open: boolean; message: string; severity: 'success' | 'error' | 'info' }) => void,
  t: (key: string, opts?: any) => string,
  err: any,
  fallback?: string
) {
  const msg = getErrorMessages(err, t, fallback).join('\n');
  setSnackbar({ open: true, message: msg, severity: 'error' });
}
